import React from "react";
import { Button } from "../components/ui/button";
import { Check, Activity, Brain, ShieldCheck } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FAQ from "../components/FAQ"; // Reusing FAQ for consistency

const ChronicPain = () => {
  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero Section */}
      <div className="relative w-full h-[600px] overflow-hidden flex items-center">
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1620051844584-15ac31d5fccd?q=80&w=2000&auto=format&fit=crop')",
          }}
        >
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 flex flex-col justify-center h-full">
          <div className="max-w-2xl text-white">
            <span className="bg-[#4caf50] text-white px-3 py-1 rounded-lg text-sm font-bold uppercase tracking-wider mb-4 inline-block">
              Condition Focus
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Specialist Care for Chronic Pain
            </h1>
            <p className="text-xl lg:text-2xl text-gray-100 mb-8 font-light leading-relaxed">
              Where conventional treatments have not provided adequate relief, a specialist clinical assessment may help determine whether medical cannabis is an appropriate option for you.
            </p>
            <Button
              className="bg-[#4caf50] hover:bg-[#3d8b40] text-white text-lg px-8 py-6 rounded-lg shadow-lg"
              onClick={() => window.location.href = "https://app.integroclinics.com/v2/registration"}
            >
              Check your eligibility
            </Button>
          </div>
        </div>
      </div>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="lg:w-1/2">
               <h2 className="text-3xl font-bold text-[#012d5c] mb-6">Understanding Chronic Pain</h2>
               <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                 Chronic pain affects millions of people in the UK. Unlike acute pain, which alerts us to injury, chronic pain persists for months or years, often becoming a condition in itself.
               </p>
               <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                 At Integro Clinics, we provide specialist clinical assessments for patients with complex chronic pain where standard therapies have not provided adequate relief. Whether medical cannabis may be an appropriate option is determined individually by the prescribing clinician following a thorough clinical assessment and MDT review.
               </p>
               <ul className="space-y-3 mt-4">
                 {[
                   "Specialist clinical assessment at no charge",
                   "MDT review and personalised treatment planning",
                   "Discreet UK next-day delivery if a prescription is issued",
                   "Ongoing follow-up support at no additional charge"
                 ].map((item, i) => (
                   <li key={i} className="flex items-center text-[#012d5c] font-medium">
                     <Check className="w-5 h-5 text-[#4caf50] mr-3" />
                     {item}
                   </li>
                 ))}
               </ul>
            </div>
            <div className="lg:w-1/2">
                <img 
                  src="https://images.unsplash.com/photo-1589279003513-467d320f47eb?w=800&auto=format&fit=crop&q=80" 
                  alt="Doctor consulting with patient" 
                  className="rounded-lg shadow-2xl"
                />
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section className="py-20 bg-[#f4f7fa]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-[#012d5c] mb-12">Pain Conditions We Assess</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: "Fibromyalgia", icon: <Activity /> },
              { name: "Neuropathic Pain", icon: <Brain /> },
              { name: "Arthritis & Joint Pain", icon: <Activity /> },
              { name: "Migraines", icon: <Brain /> },
              { name: "Back & Neck Pain", icon: <Activity /> },
              { name: "Cancer-Related Pain", icon: <ShieldCheck /> },
              { name: "Endometriosis", icon: <Activity /> },
              { name: "CRPS", icon: <Brain /> },
            ].map((cond, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col items-center justify-center h-full">
                <div className="bg-[#e0f2fe] p-4 rounded-full mb-4 text-[#005eb8]">
                  {cond.icon}
                </div>
                <h3 className="font-bold text-[#012d5c] text-lg">{cond.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Strip */}
      <section className="py-16 bg-[#012d5c] text-white">
        <div className="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center">
          <div className="mb-8 md:mb-0 text-center md:text-left">
            <h2 className="text-3xl font-bold mb-2">Ready to explore your options?</h2>
            <p className="text-blue-100 text-lg">Check your eligibility to find out whether you may be suitable for a specialist clinical assessment.</p>
          </div>
          <Button 
            className="bg-[#4caf50] hover:bg-[#3d8b40] text-white px-8 py-6 rounded-lg text-lg shadow-lg"
             onClick={() => window.location.href = "https://app.integroclinics.com/v2/registration"}
          >
            Check your eligibility
          </Button>
        </div>
      </section>

      {/* Reused FAQ Section */}
      <FAQ />

      <Footer />
    </div>
  );
};

export default ChronicPain;
